import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import { graphqlWrite, dispatchVerifyQa } from './api'
import { ADD_COMMENT, UPDATE_ISSUE, UPDATE_ISSUE_FIELD, DELETE_ISSUE_FIELD_VALUE, SET_ISSUE_TYPE, CLOSE_ISSUE, REOPEN_ISSUE, DELETE_ISSUE, ADD_LABELS, REMOVE_LABELS, ADD_ASSIGNEES, REMOVE_ASSIGNEES } from './github-queries'
import { keys, queryClient, type IssueComment, type RepoMeta } from './query'
import {
  currentIssue,
  issueWriteOptions,
  type IssueWriteKind,
  type WriteContext,
  type WriteVars,
} from './issue-write'
import { ISSUE_FIELDS, type Assignee, type IssueField, type IssueFieldName, type WorkIssue } from '@/types/github'

// Every issue edit is a declaration over the shared lifecycle in
// issue-write.ts: its document, its patch, its Undo inverse and its toasts.
// Verify-in-QA is the exception — it dispatches a workflow, not an issue write.

const appWrite: WriteContext = { client: queryClient, send: graphqlWrite }

export type FieldName = IssueFieldName

const keyOf = (name: FieldName): keyof WorkIssue => ISSUE_FIELDS[name].key

export type FieldValue = string | number | null

type SetFieldVars = {
  issue: WorkIssue
  fieldName: FieldName
  value: FieldValue
  /** Suppress the Undo toast (used by Undo itself and by drag flows). */
  silent?: boolean
}

function cachedField(fieldName: string, client = queryClient): IssueField | undefined {
  return client
    .getQueryData<IssueField[]>(keys.issueFields)
    ?.find((f) => f.name === fieldName)
}

function buildFieldInput(field: IssueField, value: string | number): Record<string, unknown> {
  switch (field.dataType) {
    case 'single-select': {
      const option = field.options.find((o) => o.name === value)
      if (!option) throw new Error(`option "${value}" not found on ${field.name}`)
      return { fieldId: field.id, singleSelectOptionId: option.id }
    }
    case 'number':
      return { fieldId: field.id, numberValue: value }
    case 'date':
      return { fieldId: field.id, dateValue: value }
    case 'text':
      return { fieldId: field.id, textValue: value }
  }
}

export const setFieldWrite: IssueWriteKind<SetFieldVars> = {
  send: async ({ issue, fieldName, value }, { client, send }) => {
    const field = cachedField(fieldName, client)
    if (!field) throw new Error(`field "${fieldName}" not loaded yet`)
    await send(
      value === null
        ? { query: DELETE_ISSUE_FIELD_VALUE, variables: { issueId: issue.id, fieldId: field.id }, preview: 'issue_fields' }
        : { query: UPDATE_ISSUE_FIELD, variables: { issueId: issue.id, field: buildFieldInput(field, value) }, preview: 'issue_fields' },
    )
  },
  patch: ({ fieldName, value }) => ({ [keyOf(fieldName)]: value }),
  undo: (issue, before, vars) => ({ ...vars, issue, value: (before[keyOf(vars.fieldName)] as FieldValue | undefined) ?? null }),
  success: ({ issue, fieldName, value }) => `#${issue.number} ${fieldName} ${value === null ? 'cleared' : `→ ${value}`}`,
  failure: ({ issue, fieldName }) => `Couldn't update ${fieldName} on #${issue.number}`,
}

export function useSetIssueField() {
  return useMutation(issueWriteOptions(appWrite, setFieldWrite))
}

type SetTypeVars = { issue: WorkIssue; typeName: string | null; silent?: boolean }

export const setTypeWrite: IssueWriteKind<SetTypeVars> = {
  send: async ({ issue, typeName }, { client, send }) => {
    let issueTypeId: string | null = null
    if (typeName !== null) {
      const types = client.getQueryData<RepoMeta>(keys.repoMeta)?.issueTypes
      issueTypeId = types?.find((t) => t.name === typeName)?.id ?? null
      if (!issueTypeId) throw new Error(`issue type "${typeName}" not loaded yet`)
    }
    await send({ query: SET_ISSUE_TYPE, variables: { id: issue.id, issueTypeId } })
  },
  patch: ({ typeName }) => ({ typeName }),
  undo: (issue, before, vars) => ({ ...vars, issue, typeName: before.typeName ?? null }),
  success: ({ issue, typeName }) => `#${issue.number} Type ${typeName === null ? 'cleared' : `→ ${typeName}`}`,
  failure: ({ issue }) => `Couldn't update Type on #${issue.number}`,
}

/** Set or clear the native GitHub Issue Type. */
export function useSetIssueType() {
  return useMutation(issueWriteOptions(appWrite, setTypeWrite))
}

type TextVars = { issue: WorkIssue; title?: string; body?: string; silent?: boolean }

export const textWrite: IssueWriteKind<TextVars> = {
  send: async ({ issue, title, body }, { send }) => {
    await send({ query: UPDATE_ISSUE, variables: { id: issue.id, title, body } })
  },
  patch: ({ title, body }) => ({ ...(title !== undefined && { title }), ...(body !== undefined && { body }) }),
  success: ({ issue }) => `#${issue.number} updated`,
  failure: ({ issue }) => `Couldn't update #${issue.number}`,
}

export function useUpdateIssueText() {
  return useMutation(issueWriteOptions(appWrite, textWrite))
}

/** Where a state write takes the issue; the hooks fix it, Undo overrides it. */
type StateTarget = 'COMPLETED' | 'NOT_PLANNED' | 'OPEN'
type StateVars = { issue: WorkIssue; silent?: boolean; to?: StateTarget }

/**
 * Close (as completed or not planned) or reopen. A closed issue leaves the
 * open-only universe but stays CLOSED in the detail cache so the Sheet can
 * offer Reopen.
 */
function stateWrite(target: StateTarget): IssueWriteKind<StateVars> {
  const to = (vars: StateVars) => vars.to ?? target
  return {
    send: async (vars, { send }) => {
      const t = to(vars)
      await send(
        t === 'OPEN'
          ? { query: REOPEN_ISSUE, variables: { id: vars.issue.id } }
          : { query: CLOSE_ISSUE, variables: { id: vars.issue.id, reason: t } },
      )
    },
    patch: (vars) => {
      const t = to(vars)
      return t === 'OPEN' ? { state: 'OPEN', stateReason: 'REOPENED' } : { state: 'CLOSED', stateReason: t }
    },
    undo: (issue, before, vars) => ({
      ...vars,
      issue,
      to: to(vars) !== 'OPEN' ? 'OPEN' : before.stateReason === 'NOT_PLANNED' ? 'NOT_PLANNED' : 'COMPLETED',
    }),
    success: (vars) =>
      ({
        COMPLETED: `#${vars.issue.number} closed as completed`,
        NOT_PLANNED: `#${vars.issue.number} archived (closed as not planned)`,
        OPEN: `#${vars.issue.number} reopened`,
      })[to(vars)],
    failure: (vars) =>
      ({
        COMPLETED: `Couldn't close #${vars.issue.number}`,
        NOT_PLANNED: `Couldn't archive #${vars.issue.number}`,
        OPEN: `Couldn't reopen #${vars.issue.number}`,
      })[to(vars)],
    dirties: [keys.closedIssues],
    moves: (vars) => (to(vars) === 'OPEN' ? 'in' : 'out'),
  }
}

export const closeWrite = stateWrite('COMPLETED')
export const archiveWrite = stateWrite('NOT_PLANNED')
export const reopenWrite = stateWrite('OPEN')

/** Archive = close as not-planned (the kanban context-menu destructive action). */
export function useArchiveIssue() {
  return useMutation(issueWriteOptions(appWrite, archiveWrite))
}

/** Close as completed — distinct from archive's not-planned. */
export function useCloseIssue() {
  return useMutation(issueWriteOptions(appWrite, closeWrite))
}

export function useReopenIssue() {
  return useMutation(issueWriteOptions(appWrite, reopenWrite))
}

/**
 * "Verified in QA" — dispatches the work repo's verify-qa workflow so the bot
 * applies `system/verified-qa` (the browser can't: the label guard strips
 * hand-applied `system/*`). The marker lands once the workflow runs, so we
 * refetch shortly after to surface it — no optimistic patch (we don't own the write).
 */
export function useVerifyQa() {
  const mutation = useMutation({
    mutationFn: async ({ issue }: { issue: WorkIssue }) => {
      await dispatchVerifyQa(issue.number)
    },
    onError: (err, { issue }) => {
      toast.error(`Couldn't verify #${issue.number} in QA`, {
        description: err instanceof Error ? err.message : undefined,
        action: { label: 'Retry', onClick: () => mutation.mutate({ issue }) },
      })
    },
    onSuccess: (_data, { issue }) => {
      toast.success(`Verifying #${issue.number} in QA…`, {
        description: 'The marker appears once the workflow runs.',
      })
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: keys.issues })
        void queryClient.invalidateQueries({ queryKey: keys.issueDetail(issue.number) })
      }, 6000)
    },
  })
  return mutation
}

export const deleteWrite: IssueWriteKind<WriteVars> = {
  send: async ({ issue }, { send }) => {
    await send({ query: DELETE_ISSUE, variables: { id: issue.id } })
  },
  patch: () => ({}),
  success: ({ issue }) => `#${issue.number} deleted`,
  failure: ({ issue }) => `Couldn't delete #${issue.number}`,
  dirties: [keys.closedIssues],
  moves: () => 'gone',
}

export function useDeleteIssue() {
  return useMutation(issueWriteOptions(appWrite, deleteWrite))
}

type CommentVars = {
  issue: WorkIssue
  body: string
  author: IssueComment['author']
  /** Names the optimistic comment so a rollback removes exactly it. */
  optimisticId: string
}

export const commentWrite: IssueWriteKind<CommentVars> = {
  send: async ({ issue, body }, { send }) => {
    await send({ query: ADD_COMMENT, variables: { subjectId: issue.id, body } })
  },
  patch: ({ issue }, { client }) => ({ comments: currentIssue(client, issue).comments + 1 }),
  patchDetail: (detail, { body, author, optimisticId }) => ({
    ...detail,
    comments: [...detail.comments, { id: optimisticId, author, createdAt: new Date().toISOString(), body }],
  }),
  unpatchDetail: (detail, { optimisticId }) => ({
    ...detail,
    comments: detail.comments.filter((c) => c.id !== optimisticId),
  }),
  failure: ({ issue }) => `Couldn't comment on #${issue.number}`,
  // The text stays in the composer on failure; a toast Retry would post it without clearing the box.
  retry: false,
}

export function useAddComment() {
  return useMutation(issueWriteOptions(appWrite, commentWrite))
}

type RepoMetaCache = {
  labels: Array<{ id: string; name: string }>
  assignableUsers: Array<{ id: string; login: string; name: string | null; avatarUrl: string }>
}

function repoMeta(client: WriteContext['client']): RepoMetaCache | undefined {
  return client.getQueryData<RepoMetaCache>(keys.repoMeta)
}

/**
 * Take back one set-replacing write from `current`: drop what it added,
 * restore what it removed, keep everything anyone else changed since.
 */
function unapply(current: string[], before: string[], written: string[]): string[] {
  const added = new Set(written.filter((n) => !before.includes(n)))
  const removed = before.filter((n) => !written.includes(n) && !current.includes(n))
  return [...current.filter((n) => !added.has(n)), ...removed]
}

/** Add/remove IDs to turn `from` into `to`; throws if any name has no ID. */
function diffIds(from: string[], to: string[], idOf: Map<string, string>, what: string) {
  const ids = (names: string[]) =>
    names.map((n) => {
      const id = idOf.get(n)
      if (!id) throw new Error(`unknown ${what}`)
      return id
    })
  const fromSet = new Set(from)
  const toSet = new Set(to)
  return { addIds: ids(to.filter((n) => !fromSet.has(n))), removeIds: ids(from.filter((n) => !toSet.has(n))) }
}

type SetLabelsVars = { issue: WorkIssue; labels: string[]; silent?: boolean }

/** Replace-the-set semantics over GitHub's add/remove label mutations. */
export const setLabelsWrite: IssueWriteKind<SetLabelsVars> = {
  send: async ({ issue, labels }, { client, send }) => {
    const all = repoMeta(client)?.labels
    if (!all) throw new Error('labels not loaded yet')
    const { addIds, removeIds } = diffIds(issue.labels, labels, new Map(all.map((l) => [l.name, l.id])), 'label')
    if (addIds.length) await send({ query: ADD_LABELS, variables: { labelableId: issue.id, labelIds: addIds } })
    if (removeIds.length) await send({ query: REMOVE_LABELS, variables: { labelableId: issue.id, labelIds: removeIds } })
  },
  patch: ({ labels }) => ({ labels }),
  undo: (issue, before, { labels }) => ({ issue, labels: unapply(issue.labels, before.labels ?? [], labels) }),
  success: ({ issue }) => `#${issue.number} labels updated`,
  failure: ({ issue }) => `Couldn't update labels on #${issue.number}`,
}

export function useSetLabels() {
  return useMutation(issueWriteOptions(appWrite, setLabelsWrite))
}

type SetAssigneesVars = { issue: WorkIssue; logins: string[]; silent?: boolean }

export const setAssigneesWrite: IssueWriteKind<SetAssigneesVars> = {
  send: async ({ issue, logins }, { client, send }) => {
    const users = repoMeta(client)?.assignableUsers
    if (!users) throw new Error('assignable users not loaded yet')
    const current = issue.assignees.map((a) => a.login)
    const { addIds, removeIds } = diffIds(current, logins, new Map(users.map((u) => [u.login, u.id])), 'assignee')
    if (addIds.length) await send({ query: ADD_ASSIGNEES, variables: { assignableId: issue.id, assigneeIds: addIds } })
    if (removeIds.length) await send({ query: REMOVE_ASSIGNEES, variables: { assignableId: issue.id, assigneeIds: removeIds } })
  },
  patch: ({ logins }, { client }) => {
    const byLogin = new Map((repoMeta(client)?.assignableUsers ?? []).map((u) => [u.login, u]))
    const assignees: Assignee[] = logins.map((login) => {
      const u = byLogin.get(login)
      return { login, name: u?.name ?? null, avatarUrl: u?.avatarUrl ?? '' }
    })
    return { assignees }
  },
  undo: (issue, before, { logins }) => ({
    issue,
    logins: unapply(issue.assignees.map((a) => a.login), (before.assignees ?? []).map((a) => a.login), logins),
  }),
  success: ({ issue }) => `#${issue.number} assignees updated`,
  failure: ({ issue }) => `Couldn't update assignees on #${issue.number}`,
}

export function useSetAssignees() {
  return useMutation(issueWriteOptions(appWrite, setAssigneesWrite))
}
